import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { getColorInfoAndDelete, colorInfoToCss } from "../colors/color";

/** Hidden layer name for soft-deleted entities during undo */
export const UNDO_LAYER_NAME = "__DR_UNDO_TEMP";

/** Internal layer prefixes to hide from UI */
const INTERNAL_LAYER_PREFIXES = ["__DR_"];

/**
 * Layer information for UI display.
 */
export interface LayerInfo {
  name: string;
  isVisible: boolean; // !isOff
  isFrozen: boolean;
  isLocked: boolean;
  colorIndex: number;
  /** Couleur CSS résolue (ACI ou TrueColor RGB), `undefined` si non résolvable. */
  colorCss?: string;
}

/**
 * Ensure the hidden undo layer exists in the database.
 * Creates it if it doesn't exist.
 */
export function ensureUndoLayerExists(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): void {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForWrite,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(UNDO_LAYER_NAME);

  const exists = layerTable.has1(layerNameStr);

  if (!exists) {
    const undoLayer = module.OdDbLayerTableRecord.createObject();
    undoLayer.setName(layerNameStr);
    undoLayer.setIsOff(true);
    undoLayer.setIsLocked(true);
    layerTable.add(undoLayer);
    deleteObj(undoLayer);
  }

  deleteObj(layerNameStr);
  deleteObj(layerTable);
}

/**
 * Check if a layer exists in the database.
 */
export function layerExists(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  const exists = layerTable.has1(layerNameStr);

  deleteObj(layerNameStr);
  deleteObj(layerTable);

  return exists;
}

/**
 * Check if a layer is internal (should be hidden from UI).
 */
function isInternalLayer(name: string): boolean {
  return INTERNAL_LAYER_PREFIXES.some((prefix) => name.startsWith(prefix));
}

/**
 * Get all layers in the database.
 * Excludes internal layers (prefixed with __DR_ or DR_).
 */
export function getAllLayers(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  /** Fond du plan, pour la couleur affichée de l'ACI 7 (cf. colorInfoToCss). */
  backgroundColor?: number,
): LayerInfo[] {
  const layers: LayerInfo[] = [];

  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const iterator = layerTable.newIterator(true, true);

  while (!iterator.done()) {
    const record = iterator.getRecord(OpenMode.kForRead, false);
    const layerRecord = module.OdDbLayerTableRecord.cast(record);

    const nameStr = layerRecord.getName();
    const name = nameStr.toString();
    deleteObj(nameStr);

    // Skip internal layers
    if (!isInternalLayer(name)) {
      layers.push({
        name,
        isVisible: !layerRecord.isOff(),
        isFrozen: layerRecord.isFrozen(),
        isLocked: layerRecord.isLocked(),
        colorIndex: layerRecord.colorIndex(),
        // Les calques de légende DR stockent leur couleur en TrueColor : lire
        // l'OdCmColor complet plutôt que le seul index ACI.
        colorCss: colorInfoToCss(
          getColorInfoAndDelete(layerRecord.color()),
          backgroundColor,
        ),
      });
    }

    deleteObj(layerRecord);
    deleteObj(record);
    iterator.step(true, true);
  }

  deleteObj(iterator);
  deleteObj(layerTable);

  return layers;
}

/**
 * Set layer visibility (on/off).
 * @param visible true = visible (off=false), false = hidden (off=true)
 */
export function setLayerVisibility(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  visible: boolean,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    deleteObj(layerNameStr);
    deleteObj(layerTable);
    return false;
  }

  const layerId = layerTable.getAt3(layerNameStr, false);
  deleteObj(layerNameStr);
  deleteObj(layerTable);

  const layerRecord = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );
  layerRecord.setIsOff(!visible);
  deleteObj(layerRecord);

  return true;
}

/**
 * Set layer frozen state.
 * @param frozen true = frozen, false = thawed
 */
export function setLayerFrozen(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  frozen: boolean,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    deleteObj(layerNameStr);
    deleteObj(layerTable);
    return false;
  }

  const layerId = layerTable.getAt3(layerNameStr, false);
  deleteObj(layerNameStr);
  deleteObj(layerTable);

  const layerRecord = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );
  layerRecord.setIsFrozen(frozen);
  deleteObj(layerRecord);

  return true;
}

/**
 * Set layer locked state (visible but not selectable/editable).
 * @param locked true = locked, false = unlocked
 */
export function setLayerLocked(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  locked: boolean,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    deleteObj(layerNameStr);
    deleteObj(layerTable);
    return false;
  }

  const layerId = layerTable.getAt3(layerNameStr, false);
  deleteObj(layerNameStr);
  deleteObj(layerTable);

  const layerRecord = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );
  layerRecord.setIsLocked(locked);
  deleteObj(layerRecord);

  return true;
}

/** Noms des calques verrouillés (pour filtrer la sélection). */
export function getLockedLayerNames(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): Set<string> {
  const set = new Set<string>();
  const layerTable = openAs(
    db.getLayerTableId(),
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );
  const iterator = layerTable.newIterator(true, true);
  while (!iterator.done()) {
    const record = iterator.getRecord(OpenMode.kForRead, false);
    const layerRecord = module.OdDbLayerTableRecord.cast(record);
    if (layerRecord.isLocked()) {
      const nameStr = layerRecord.getName();
      set.add(nameStr.toString());
      deleteObj(nameStr);
    }
    deleteObj(layerRecord);
    deleteObj(record);
    iterator.step(true, true);
  }
  deleteObj(iterator);
  deleteObj(layerTable);
  return set;
}
